import React, { useState, useEffect } from "react";
import { CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Button } from "./ui/button";
import { ArrowUpDown, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "./ui/pagination";
import fetchWithAuth from "../fetchWithAuth";

const reportDateFormatter = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow", year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23",
});

function formatReportDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "-";
  const parts = Object.fromEntries(reportDateFormatter.formatToParts(date).map(({ type, value }) => [type, value]));
  return `${parts.day}.${parts.month}.${parts.year} ${parts.hour}:${parts.minute}`;
}

export default function ReportTable({ projectId }) {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sorting, setSorting] = useState({ column: "date", direction: "desc" });
  const [queries, setQueries] = useState([]);
  const [promotedPages, setPromotedPages] = useState([]);
  const itemsPerPage = 50;

  // Кэш страниц: ключ - JSON.stringify({page, sort, queries}), значение - {items, total}
  const pageCacheRef = React.useRef({});

  // Кэшируем queries для projectId и загружаем продвигаемые страницы
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError("");
    
    // Загружаем данные проекта и продвигаемые страницы параллельно
    Promise.all([
      fetchWithAuth(`/api/project/${projectId}`).then(res => res.json()),
      fetchWithAuth(`/api/project/${projectId}/promoted`).then(res => res.json())
    ])
      .then(([projectData, promotedPagesData]) => {
        if (!isMounted) return;
        
        // Обрабатываем queries
        if (!projectData.queries || projectData.queries.length === 0) {
          setError("У проекта нет запросов для отчета");
          setQueries([]);
          setLoading(false);
        } else {
          setQueries(projectData.queries.map(q => q.queryText).filter(Boolean));
        }
        
        // Обрабатываем продвигаемые страницы
        if (promotedPagesData && Array.isArray(promotedPagesData)) {
          setPromotedPages(promotedPagesData.map(page => page.url).filter(Boolean));
        }
      })
      .catch(err => {
        if (isMounted) {
          setError("Ошибка при загрузке данных проекта");
          setQueries([]);
          setPromotedPages([]);
          setLoading(false);
        }
      });
    return () => { isMounted = false; };
  }, [projectId]);

  // Загружаем отчет только когда queries готовы, с prefetch и кэшем страниц
  useEffect(() => {
    if (!queries || queries.length === 0) {
      setReportData([]);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    let isMounted = true;
    const cacheKey = JSON.stringify({
      projectId,
      page: currentPage,
      sort: sorting,
      queries,
    });

    // Если страница есть в кэше — показываем мгновенно
    if (pageCacheRef.current[cacheKey]) {
      const cached = pageCacheRef.current[cacheKey];
      setReportData(cached.items || []);
      setTotalPages(Math.ceil(cached.total / itemsPerPage) || 1);
      setLoading(false);
    } else {
      setLoading(true);
      setError("");
      fetchWithAuth(`/api/report-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          queryTexts: queries,
          page: currentPage,
          limit: itemsPerPage,
          sortBy: sorting.column,
          sortDirection: sorting.direction,
        }),
      })
        .then(async res => {
          if (!res.ok) {
            const errorData = await res.json();
            throw new Error(errorData.error || "Ошибка при загрузке данных отчета");
          }
          return res.json();
        })
        .then(data => {
          if (isMounted) {
            setReportData(data.items || []);
            setTotalPages(Math.ceil(data.total / itemsPerPage) || 1);
            setLoading(false);
            // Кэшируем страницу
            pageCacheRef.current[cacheKey] = {
              items: data.items || [],
              total: data.total,
            };
          }
        })
        .catch(err => {
          if (isMounted) {
            console.error("Ошибка при загрузке отчета:", err);
            setError(err.message || "Ошибка при загрузке данных отчета");
            setLoading(false);
          }
        });
    }

    // Prefetch следующей страницы
    const nextPage = currentPage + 1;
    if (nextPage <= totalPages) {
      const nextCacheKey = JSON.stringify({
        projectId,
        page: nextPage,
        sort: sorting,
        queries,
      });
      if (!pageCacheRef.current[nextCacheKey]) {
        fetchWithAuth(`/api/report-data`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            projectId,
            queryTexts: queries,
            page: nextPage,
            limit: itemsPerPage,
            sortBy: sorting.column,
            sortDirection: sorting.direction,
          }),
        })
          .then(async res => {
            if (!res.ok) return;
            const data = await res.json();
            pageCacheRef.current[nextCacheKey] = {
              items: data.items || [],
              total: data.total,
            };
          })
          .catch(() => {});
      }
    }

    return () => { isMounted = false; };
  }, [projectId, currentPage, sorting, queries]);

  const handleSort = (column) => {
    setSorting(prev => ({
      column,
      direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Нормализация URL для сравнения (убираем протокол, www и завершающий слэш)
  const normalizeUrl = (url) => {
    if (!url) return "";
    return url.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "").toLowerCase();
  };

  // Проверка, есть ли страница в списке продвигаемых
  const isPromotedPage = (url) => {
    if (!url || !promotedPages.length) return false;
    const normalizedUrl = normalizeUrl(url);
    return promotedPages.some(promotedUrl => 
      normalizeUrl(promotedUrl) === normalizedUrl
    );
  };

  // Получение полного URL для ссылки
  const getFullUrl = (url) => {
    if (!url) return "#";
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  // Генерация элементов пагинации
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // Первая страница
    if (startPage > 1) {
      items.push(
        <PaginationItem key="first">
          <PaginationLink 
            onClick={() => handlePageChange(1)}
            size="icon"
          >
            1
          </PaginationLink>
        </PaginationItem>
      );
      
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }
    }
    
    // Страницы
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink 
            isActive={currentPage === i}
            onClick={() => handlePageChange(i)}
            size="icon"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    // Последняя страница
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }
      
      items.push(
        <PaginationItem key="last">
          <PaginationLink 
            onClick={() => handlePageChange(totalPages)}
            size="icon"
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  };

  return (
    <div className="space-y-4">
      <CardHeader className="px-0 pt-0">
        <CardTitle>Отчет по переходам из выдачи</CardTitle>
      </CardHeader>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">
                    <Button 
                      variant="ghost" 
                      onClick={() => handleSort("date")}
                      className="flex items-center gap-1 font-medium"
                    >
                      Дата (МСК)
                      <ArrowUpDown className="h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[200px]">
                    <Button 
                      variant="ghost" 
                      onClick={() => handleSort("ask")}
                      className="flex items-center gap-1 font-medium"
                    >
                      Запрос
                      <ArrowUpDown className="h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[120px] text-center">
                    <Button 
                      variant="ghost" 
                      onClick={() => handleSort("pos")}
                      className="flex items-center gap-1 font-medium justify-center"
                    >
                      Позиция
                      <ArrowUpDown className="h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[200px]">
                    <Button 
                      variant="ghost" 
                      onClick={() => handleSort("domain")}
                      className="flex items-center gap-1 font-medium"
                    >
                      Домен
                      <ArrowUpDown className="h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[600px]">
                    <Button 
                      variant="ghost" 
                      onClick={() => handleSort("url")}
                      className="flex items-center gap-1 font-medium"
                    >
                      Страница
                      <ArrowUpDown className="h-4 w-4" />
                    </Button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reportData.length > 0 ? (
                  reportData.map((item, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell className="w-[180px]">
                          {formatReportDateTime(item.date)}
                        </TableCell>
                        <TableCell>{item.ask}</TableCell>
                        <TableCell className="w-[120px] text-center">{item.pos}</TableCell>
                        <TableCell className="w-[200px]">{item.domain}</TableCell>
                        <TableCell 
                          className="w-[600px]"
                          style={{
                            backgroundColor: isPromotedPage(item.url) ? '#dcfce7' : '#fee2e2'
                          }}
                        >
                          {item.url ? (
                            <a 
                              href={getFullUrl(item.url)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline break-all"
                            >
                              {item.url}
                            </a>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                      Нет данных для отображения
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          {reportData.length > 0 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                  </PaginationItem>
                  
                  {renderPaginationItems()}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
}
